/**
 * COMPONENTE DE LAYOUT DE PORTADA PARA AUTENTICACIÓN
 * 
 * Este componente proporciona un layout especializado para páginas de autenticación
 * con imagen de fondo, posicionamiento centrado de contenido y branding corporativo
 * integrado, optimizado para formularios de login, registro y recuperación de contraseña.
 * 
 * CARACTERÍSTICAS PRINCIPALES v3.0:
 * - Layout responsivo con imagen de fondo personalizable
 * - Posicionamiento automático del logo corporativo (Cuadrante H)
 * - Soporte para modo oscuro con filtros automáticos de imagen
 * - Grid system adaptativo para diferentes tamaños de pantalla
 * - Contenedor centrado con dimensiones personalizables
 * - Estructura limpia sin navbar para experiencia de autenticación enfocada
 * 
 * FUNCIONALIDADES PRINCIPALES:
 * - **Imagen de Portada**: Background full-cover con altura personalizable
 * - **Logo Corporativo**: Posicionamiento fijo en esquina superior izquierda
 * - **Contenedor Responsivo**: Grid adaptativo con breakpoints personalizables
 * - **Tema Dinámico**: Adaptación automática a modo claro/oscuro
 * - **Layout Centrado**: Posicionamiento automático del contenido principal
 * 
 * ESTRUCTURA DEL LAYOUT v3.0:
 * 1. PageLayout como contenedor principal
 * 2. Logo corporativo posicionado absolutamente (top-left)
 * 3. Imagen de fondo con cobertura completa y altura personalizable
 * 4. Contenedor centrado para contenido (formularios de auth)
 * 5. Footer opcional (actualmente comentado)
 * 
 * RESPONSIVIDAD:
 * - Logo: 220px en todos los breakpoints
 * - Contenedor: Anchos adaptativos por pantalla
 *   * xs: 11/12 columnas (91.6% ancho)
 *   * sm: 9/12 columnas (75% ancho) 
 *   * md: 5/12 columnas (41.6% ancho)
 *   * lg: 4/12 columnas (33.3% ancho)
 *   * xl: 3/12 columnas (25% ancho)
 * 
 * PROPS PERSONALIZABLES:
 * - coverHeight: Altura del área de imagen de fondo
 * - image: URL de la imagen de background
 * - children: Contenido a renderizar (formularios)
 * - customWidth: Objeto con breakpoints personalizados
 * 
 * CARACTERÍSTICAS DE UX v3.0:
 * - Transición suave entre temas (claro/oscuro)
 * - Logo con filtros automáticos para contraste óptimo
 * - Espaciado consistente con sistema de grillas Material UI
 * - Márgenes negativos para superponer contenido sobre background
 * - Bordes redondeados (xl) para estética moderna
 * 
 * OPTIMIZACIONES:
 * - Logo con object-fit: contain para mantener proporción
 * - Background con cover para llenar completamente el área
 * - Z-index optimizado para layering correcto
 * - Filtros CSS para compatibilidad con temas
 * 
 * USO TÍPICO:
 * ```jsx
 * <CoverLayout 
 *   image="/path/to/background.jpg"
 *   coverHeight="40vh"
 *   customWidth={{ md: 6, lg: 5 }}
 * >
 *   <LoginForm />
 * </CoverLayout>
 * ```
 * 
 * INTEGRACIÓN CON SISTEMA:
 * - Compatible con Material Dashboard 2 React v2.2.0
 * - Utiliza componentes MDBox para consistencia visual
 * - Integrado con sistema de temas MUI
 * - Logo corporativo Cuadrante H pre-configurado
 * 
 * @component
 * @param {string} coverHeight - Altura del área de imagen de fondo (default: "35vh")
 * @param {string} image - URL de la imagen de background (requerido)
 * @param {React.ReactNode} children - Contenido a renderizar dentro del layout (requerido)
 * @param {Object} customWidth - Breakpoints personalizados para ancho del contenedor
 * @returns {JSX.Element} Layout de portada para páginas de autenticación
 * 
 * @author Diego Alejandro
 * @version 3.0
 * @since 2025-08
 * @updated 2025-08 - Eliminación de navbar, reposicionamiento de logo, optimización responsiva
 */

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
// import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import PageLayout from "examples/LayoutContainers/PageLayout";
import { useTheme } from '@mui/material/styles';

// Authentication layout components
import Footer from "layouts/authentication/components/Footer";

import QuadranteHLogo from '../../../../assets/images/logo_koop/logo Quadrante H.png';

function CoverLayout({ coverHeight, image, children, customWidth }) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <PageLayout>
      {/* ELIMINÉ EL NAVBAR DE AQUÍ - DefaultNavbar que contenía Dashboard, Perfil, Cuadrante H, etc. */}
      
      {/* LOGO EN LA ESQUINA SUPERIOR DERECHA */}
<MDBox
  position="absolute"
  top={60}
  left={60} // Changed from right={20} to left={20}
  zIndex={1000}
  sx={{
    width: { xs: "220px", md: "220px" },
    height: "auto",
  }}
>
  <img
    src={QuadranteHLogo}
    alt="Cuadrante H Logo"
    style={{
      width: "100%",
      height: "auto",
      objectFit: "contain",
      filter: isDarkMode ? 'invert(1) grayscale(1) brightness(0.8)' : 'none',
    }}
  />
</MDBox>
      
<MDBox
  width="calc(100% - 2rem)"
  minHeight={coverHeight}
  borderRadius="xl"
  mx={2}
  my={2}
  pt={6}
  pb={28}
  sx={{
    backgroundImage: `url(${image})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  }}
/>
      
      <MDBox mt={{ xs: -20, lg: -18 }} px={1} width="calc(100% - 2rem)" mx="auto">
        <Grid container spacing={1} justifyContent="center">
          <Grid item xs={customWidth?.xs || 11} sm={customWidth?.sm || 9} md={customWidth?.md || 5} lg={customWidth?.lg || 4} xl={customWidth?.xl || 3}>
            {children}
          </Grid>
        </Grid>
      </MDBox>
      
      {/* AQUÍ ESTÁ EL FOOTER ORIGINAL - Footer component que viene del archivo layouts/authentication/components/Footer */}
      {/* <Footer /> */}
    </PageLayout>
  );
}

// Setting default props for the CoverLayout
CoverLayout.defaultProps = {
  coverHeight: "35vh",
  customWidth: null,
};

// Typechecking props for the CoverLayout
CoverLayout.propTypes = {
  coverHeight: PropTypes.string,
  image: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  customWidth: PropTypes.object,
};

export default CoverLayout;